import React, { useLayoutEffect } from "react";
import { FlexibleSelectData } from "./type";
import { useState, useMemo } from "react";
import useVirtualList from "./useVirtualList";

/** 下拉面板最大宽度，避免商品名称过长时撑出可视区域 */
const MAX_CONTAINER_WIDTH = 850;

/** 使用字符串的长度作为依据，计算最大宽度，但是不一定准确，因为中英文数字的占宽是不一致的 */
function findLongestDataItem(data: FlexibleSelectData[]) {
    if (!data?.length) return undefined;

    let maxIndex = 0;
    for (let i = 1; i < data.length; i++) {
        const maxLengthString = data[maxIndex].inputText;
        const str = data[i].inputText;
        if (str.length > maxLengthString.length) {
            maxIndex = i;
        }
    }

    return data[maxIndex];
}

const useLinkVirtualList = (
    data: FlexibleSelectData[],
    id: string,
    itemHeight: number,
    preventComputed: boolean,
    needSimpleTitle: boolean,
    needSkuSimpleTitle: boolean,
    showTitlePic: boolean,
) => {
    /** 文本最长的数据 */
    const [measureData, setMeasureData] = useState([]);
    /** 渲染到页面的数据 */
    const [renderData, setRenderData] = useState([]);
    const [containerWidth, setContainerWidth] = useState<number>();

    const { dataSourceValueStr, inputTextValueStr } = useMemo(() => {
        const valueList = [];
        const inputTextList = [];

        data?.forEach(({ inputText, value }) => {
            valueList.push(value);
            inputTextList.push(inputText);
        });

        return {
            dataSourceValueStr: valueList.join(),
            inputTextValueStr: inputTextList.join(),
        };
    }, [data]);

    /**
     * 数据源切换需要回到顶部
     */
    useLayoutEffect(() => {
        scrollTo(0);
    }, [dataSourceValueStr, needSimpleTitle, needSkuSimpleTitle, showTitlePic]);

    /** 确保每一次的数据源是最新的，如果需要计算宽度，在下个hooks中进行计算 */
    useLayoutEffect(() => {
        setRenderData(data);
    }, [data]);

    /**
     * 需要重新计算宽度的场景
     * 1. 简称切换 (数据源的inputText会变化)
     * 2. 爆款切换 (itemHeight会变化)
     * 3. 数据源发生变化(非搜索的情况,数据源的value会发生变化)
     */
    const recomputedWidthKey = dataSourceValueStr + inputTextValueStr + itemHeight + showTitlePic;

    useLayoutEffect(() => {
        const item = findLongestDataItem(data);

        /** 不用计算宽度，直接渲染(搜索场景) */
        if (preventComputed) {
            return;
        }

        /** 如果无数据，回到顶部，并且设置渲染的数据源为传入的数据源 */
        if (item === undefined) {
            return;
        }

        /** 找到长度最大的数据 */
        setMeasureData([item]);

        // 当高度发生变化时意味着爆款模式切换，需要重新计算
    }, [recomputedWidthKey]);

    useLayoutEffect(() => {
        if (!measureData.length) return;

        /** 开始计算前，重置宽度 */
        setContainerWidth(undefined);
        setRenderData(measureData);

        const timer1 = setTimeout(() => {
            const container = document.getElementById(id);

            if (container && container.scrollWidth) {
                // 预留滚动条宽度 + 商品名称中【多名称】的 Tag，同时不超过最大宽度
                const nextWidth = Math.min(MAX_CONTAINER_WIDTH, Math.max(500, container.scrollWidth + 30 + 80));
                /** 记录最长宽度 */
                setContainerWidth(nextWidth);
            } else {
                setContainerWidth(containerWidth);
            }

            /** 完成宽度测量，正常渲染数据 */
            setRenderData(data);
        }, 60);

        return () => {
            clearTimeout(timer1);
        };
    }, [measureData]);

    const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(renderData, {
        itemHeight,
        overscan: 8,
    });

    return {
        list,
        containerProps,
        wrapperProps,
        containerWidth,
    };
};

export default useLinkVirtualList;
